import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGroupSessions } from '@/hooks/useGroupSessions';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { ArrowLeft, Save, Trash2, Users, SplitSquareHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import ParticipantsEditor from '@/components/group/ParticipantsEditor';
import type { GroupSession, UpdateGroupSessionRequest } from '@/types/groupSession';
import type { Participant } from '@/types/category';
import { parseFullName, isFullName } from '@/utils/nameParser';

const getGroupTypeIcon = (type: string) => {
  switch (type) {
    case 'family': return '👨‍👩‍👧‍👦';
    case 'yoga': return '🧘‍♀️';
    case 'organization': return '🏢';
    case 'team': return '👥';
    case 'friends': return '👫';
    default: return '📁';
  }
};

export default function GroupDetailPage() {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const { groupSessions, updateGroupSession, deleteGroupSession, loading } = useGroupSessions();
  
  const [group, setGroup] = useState<GroupSession | null>(null);
  const [formData, setFormData] = useState<UpdateGroupSessionRequest>({});
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (groupId && groupSessions.length > 0) {
      const foundGroup = groupSessions.find(g => g.id === groupId);
      if (foundGroup) {
        setGroup(foundGroup);
        setFormData({
          group_name: foundGroup.group_name,
          group_type: foundGroup.group_type,
          description: foundGroup.description || '',
          participants: foundGroup.participants || [],
          yoga_directives: foundGroup.yoga_directives || '',
          opening_positions: foundGroup.opening_positions || '',
          ending_positions: foundGroup.ending_positions || '',
          session_timing: foundGroup.session_timing || 'standalone'
        });
      } else {
        toast.error('Group not found');
        navigate('/groups');
      }
    }
  }, [groupId, groupSessions, navigate]);

  const handleSave = async () => {
    if (!group || !groupId) return;
    
    try {
      setIsSaving(true);
      await updateGroupSession(groupId, formData);
      setIsEditing(false);
      toast.success('Group updated successfully');
    } catch (error) {
      toast.error('Failed to update group');
      console.error('Error updating group:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!groupId) return;
    
    try {
      await deleteGroupSession(groupId);
      toast.success('Group deleted successfully');
      navigate('/groups');
    } catch (error) {
      toast.error('Failed to delete group');
      console.error('Error deleting group:', error);
    }
  };

  const handleParticipantsChange = (participants: Participant[]) => {
    setFormData(prev => ({
      ...prev,
      participants
    }));
  };

  const handleSplitNames = () => {
    if (!formData) return;

    const updatedParticipants = formData.participants?.map(participant => {
      // Only split if firstName contains spaces and lastName is empty
      if (isFullName(participant.firstName) && !participant.lastName?.trim()) {
        const parsed = parseFullName(participant.firstName);
        return {
          ...participant,
          firstName: parsed.firstName,
          lastName: parsed.lastName || ''
        };
      }
      return participant;
    }) || [];

    setFormData(prev => ({
      ...prev,
      participants: updatedParticipants
    }));

    toast.success('Names split successfully! Only first names will be used in sessions.');
  };

  if (loading || !group) {
    return (
      <div className="container mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-64 bg-muted rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/groups')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Groups
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-2xl">{getGroupTypeIcon(group.group_type)}</span>
            <h1 className="text-3xl font-bold tracking-tight">{group.group_name}</h1>
          </div>
        </div>
        
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button 
                variant="secondary" 
                onClick={handleSplitNames}
                className="flex items-center gap-2"
                title="Split any full names in the First Name field into separate First and Last names"
              >
                <SplitSquareHorizontal className="h-4 w-4" />
                Split Full Names
              </Button>
              <Button onClick={handleSave} disabled={isSaving}>
                <Save className="h-4 w-4 mr-2" />
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setIsEditing(true)}>
                Edit Group
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Group
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Group</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete "{group.group_name}"? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                      Delete Group
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Group Information</CardTitle>
            <CardDescription>Basic details about your group</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="group_name">Group Name</Label>
                {isEditing ? (
                  <Input
                    id="group_name"
                    value={formData.group_name || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, group_name: e.target.value }))}
                    placeholder="Enter group name"
                  />
                ) : (
                  <p className="text-sm p-2 bg-muted rounded">{group.group_name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="group_type">Group Type</Label>
                {isEditing ? (
                  <Select
                    value={formData.group_type || ''}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, group_type: value as any }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select group type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="family">👨‍👩‍👧‍👦 Family</SelectItem>
                      <SelectItem value="friends">👫 Friends</SelectItem>
                      <SelectItem value="team">👥 Team</SelectItem>
                      <SelectItem value="organization">🏢 Organization</SelectItem>
                      <SelectItem value="yoga">🧘‍♀️ Yoga</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge className="capitalize">{group.group_type}</Badge>
                )}
              </div>

              <div className="space-y-2 md:col-span-1">
                <Label className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Participants ({formData.participants?.length || 0})
                </Label>
                <div className="text-sm p-2 bg-muted rounded">
                  {formData.participants?.length || 0} members
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              {isEditing ? (
                <Textarea
                  id="description"
                  value={formData.description || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe your group (optional)"
                  rows={3}
                />
              ) : (
                <p className="text-sm p-2 bg-muted rounded min-h-[60px]">
                  {group.description || 'No description provided'}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {isEditing && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Group Members & Biographies
              </CardTitle>
              <CardDescription>
                Add detailed information about each group member including their personal biography. 
                This helps the AI create more personalized sessions for each individual.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ParticipantsEditor 
                participants={formData.participants || []} 
                onChange={handleParticipantsChange}
              />
            </CardContent>
          </Card>
        )}

        {!isEditing && formData.participants && formData.participants.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Group Members
              </CardTitle>
              <CardDescription>Current group members</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3">
                {formData.participants.map((participant, index) => (
                  <div key={index} className="p-3 border rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium">{participant.firstName}</span>
                          {participant.lastName && (
                            <span className="text-muted-foreground">({participant.lastName})</span>
                          )}
                          {participant.preferredName && (
                            <span className="text-muted-foreground text-sm">({participant.preferredName})</span>
                          )}
                          {participant.pronouns && (
                            <Badge variant="outline" className="text-xs">{participant.pronouns}</Badge>
                          )}
                        </div>
                        <div className="grid gap-1 text-sm text-muted-foreground">
                          {participant.roleTitle && (
                            <span>Role: {participant.roleTitle}</span>
                          )}
                          {participant.subgroup && (
                            <span>Team: {participant.subgroup}</span>
                          )}
                          {participant.locationTag && (
                            <span>Location: {participant.locationTag}</span>
                          )}
                          {participant.hopeForSession && (
                            <div className="mt-2">
                              <strong>Hope for session:</strong> {participant.hopeForSession}
                            </div>
                          )}
                          {participant.backgroundStory && (
                            <div className="mt-2">
                              <strong>Background:</strong> {participant.backgroundStory}
                            </div>
                          )}
                        </div>
                      </div>
                      <Badge variant={participant.consentToName ? "default" : "secondary"}>
                        {participant.consentToName ? "Name consent ✓" : "No name consent"}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {formData.group_type === 'yoga' && (
          <Card>
            <CardHeader>
              <CardTitle>Yoga-Specific Settings</CardTitle>
              <CardDescription>Configure yoga session preferences</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="opening_positions">Opening Positions</Label>
                  {isEditing ? (
                    <Textarea
                      id="opening_positions"
                      value={formData.opening_positions || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, opening_positions: e.target.value }))}
                      placeholder="Starting yoga positions"
                      rows={3}
                    />
                  ) : (
                    <p className="text-sm p-2 bg-muted rounded min-h-[80px]">
                      {group.opening_positions || 'Not specified'}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="yoga_directives">Yoga Directives</Label>
                  {isEditing ? (
                    <Textarea
                      id="yoga_directives"
                      value={formData.yoga_directives || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, yoga_directives: e.target.value }))}
                      placeholder="Special instructions or modifications"
                      rows={3}
                    />
                  ) : (
                    <p className="text-sm p-2 bg-muted rounded min-h-[80px]">
                      {group.yoga_directives || 'Not specified'}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ending_positions">Ending Positions</Label>
                  {isEditing ? (
                    <Textarea
                      id="ending_positions"
                      value={formData.ending_positions || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, ending_positions: e.target.value }))}
                      placeholder="Closing yoga positions"
                      rows={3}
                    />
                  ) : (
                    <p className="text-sm p-2 bg-muted rounded min-h-[80px]">
                      {group.ending_positions || 'Not specified'}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="session_timing">Session Timing</Label>
                {isEditing ? (
                  <Select
                    value={formData.session_timing || 'standalone'}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, session_timing: value as any }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pre-yoga">Pre-Yoga (before practice)</SelectItem>
                      <SelectItem value="post-yoga">Post-Yoga (after practice)</SelectItem>
                      <SelectItem value="standalone">Standalone (independent)</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge variant="outline" className="capitalize">
                    {group.session_timing || 'standalone'}
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}